// [2026-09-05] ADDED — where each product in the rail actually lives.
//
// THIS IS A PORT, NOT A DESIGN. Every value below is copied from the InCruiter account app, which
// is the app whose sidebar this rail is reproducing:
//
//   · the environment prefixes and the per-product hosts are generateUrl()
//     (frontend-account/src/utils/helper.jsx:744-836);
//   · which subdomain gets which URL — and the `/dashboard` suffix on some but not others — is the
//     branch chain in the sidebar itself
//     (frontend-account/src/component/Layout/Sidebar.jsx:95-250).
//
// Nothing here is invented. If a link is wrong, it is wrong in the account app too, and both should
// be changed together.
//
// ═══ THE BUG THIS REPLACES ═══════════════════════════════════════════════════
//
// The previous version built every link as `https://${envPrefix()}${subdomain}.incruiter.com/
// dashboard`, with envPrefix() read out of VITE_ACCOUNT_URL. That was wrong in two ways, and the
// deployed dev build shows both:
//
//   1. VITE_ACCOUNT_URL IS NOT SET IN THE DEV DEPLOYMENT. Verified 2026-09-05 by reading the live
//      bundle at https://dev.v1.proctor.incruiter.com/assets/index-BBRHqofR.js — the function
//      minified to `new URL("")`, i.e. Vite folded the variable to an empty string at build time.
//      `new URL("")` throws, the catch returns '', and every product link became a PRODUCTION host.
//      Clicking IncScreen on the dev dashboard opened the real customers' IncScreen. A link that
//      leaves the environment you are working in is worse than a broken one, because it silently
//      succeeds against live data.
//
//      The prefix is therefore taken from THIS APP'S OWN HOSTNAME first. That value cannot be unset
//      at build time, cannot disagree with the environment the app is actually serving from, and
//      needs nobody to remember to configure it.
//
//   2. NOT EVERY PRODUCT HAS ITS OWN HOST. InCVid and InCFeed have no branch in the account app's
//      sidebar — they fall through to the account portal's own /dashboard. Deriving a host from the
//      subdomain sent them to `https://incvid.incruiter.com/dashboard`, a name nobody has created.
//      The chain below reproduces the account app's real routing instead of guessing.

// The environment prefix on an InCruiter frontend host: '' on production, 'dev.v1.' on dev,
// 'test.v1.' on test/preprod, 'dev.v2.' on dev2. See generateUrl's `frontendUrlPreFix`.
//
// Derived by stripping the LAST label before `.incruiter.com` off our own hostname:
//   dev.v1.proctor.incruiter.com  ->  'dev.v1.'
//   proctor.incruiter.com         ->  ''
const HOST_PREFIX = /^(.*?)[a-z0-9-]+\.incruiter\.com$/i

function prefixFromHost(hostname) {
  const match = String(hostname || '').match(HOST_PREFIX)
  return match ? match[1] : null
}

/** The environment prefix for every InCruiter frontend host.
 *
 *  ORDER OF PREFERENCE, and the reason for it:
 *    1. our own hostname — always present, always right, nothing to configure;
 *    2. VITE_ACCOUNT_URL — an explicit override for anyone who sets it, kept so this is not a
 *       behaviour change for a deployment that already relies on it;
 *    3. 'dev.v1.' — the LOCALHOST fallback.
 *
 *  Step 3 deserves a sentence, because defaulting to a specific environment looks arbitrary. On
 *  localhost neither of the first two sources exists, so something has to be chosen, and the two
 *  candidates are not symmetric: guessing 'dev' from a developer's machine opens the wrong DEV
 *  dashboard, while guessing '' opens PRODUCTION with a real customer's session attached. One of
 *  those is a nuisance and the other is an incident, so the fallback fails toward dev. */
export function envPrefix() {
  const own = prefixFromHost(typeof window !== 'undefined' ? window.location.hostname : '')
  if (own !== null) return own

  try {
    const base = String(import.meta.env?.VITE_ACCOUNT_URL || '')
    const fromEnv = base ? prefixFromHost(new URL(base).hostname) : null
    if (fromEnv !== null) return fromEnv
  } catch { /* unset or unparseable — fall through to the localhost default */ }

  return 'dev.v1.'
}

// generateUrl's `externalUrl`, used only by InCServe, which is not on the v1/v2 naming scheme:
// '' on production, 'preprod.' on test/preprod, 'dev.' everywhere else.
// (frontend-account/src/utils/helper.jsx:749-772)
function externalPrefix() {
  const prefix = envPrefix()
  if (prefix === '') return ''
  if (prefix.startsWith('test.')) return 'preprod.'
  return 'dev.'
}

/** frontend-account/src/utils/helper.jsx:773-836, for the modes this rail can link to. */
export function generateUrl(urlMode) {
  const prefix = envPrefix()
  switch (urlMode) {
    case 'incserve':  return `https://${externalPrefix()}incserve.incruiter.com`
    case 'incnudge':  return `https://${prefix}incnudge.incruiter.com`
    case 'account':   return `https://${prefix}account.incruiter.com`
    case 'incbot':    return `https://${prefix}incbot.incruiter.com`
    case 'incscreen': return `https://${prefix}incscreen.incruiter.com`
    // [2026-09-11] ADDED — the two modes sign-out needs, both copied from the account app's own
    // generateUrl rather than composed from guesswork (frontend-account/src/utils/helper.jsx:773,
    // 801). Additive: every existing case is untouched.
    //
    // 'website' is where a logged-out person belongs — the account app ends its own logout with
    // `window.location.replace(mainWebsite + '/employer/login')` (loginSaga.jsx:348), and
    // mainWebsite IS generateUrl('website').
    case 'website':   return `https://${prefix}incruiter.com`
    // 'user_backend' carries the logout endpoint. NOTE THE PREFIX IS A DIFFERENT ONE: the account
    // app keeps `frontendUrlPreFix` and `backendUrlPreFix` as separate variables because they do
    // not match — '' vs 'api.' on production, 'dev.v1.' vs 'dev.api.' on dev. Deriving the API host
    // by string-appending to the frontend prefix would produce dev.v1.api.incruiter.com, which does
    // not exist.
    case 'user_backend':
      return `https://${backendPrefix()}incruiter.com/user-auth-system/api/v1`
    default:          return null
  }
}

/** generateUrl's `backendUrlPreFix` (frontend-account/src/utils/helper.jsx:744-769), mapped from the
 *  frontend prefix this app already derives from its own hostname.
 *
 *  The pairs are taken from that file verbatim and are deliberately NOT a transformation:
 *      ''         -> 'api.'          (prod)
 *      'test.v1.' -> 'test.api.'     (test / preprod)
 *      'dev.v2.'  -> 'dev.v2.api.'   (dev2)
 *      'dev.v1.'  -> 'dev.api.'      (dev — note it is NOT 'dev.v1.api.')
 *
 *  That last one is the whole reason this is a table. Anything unrecognised falls to the dev API for
 *  the same reason envPrefix falls to dev: the wrong dev host is a nuisance, the wrong prod host is
 *  an incident. */
function backendPrefix() {
  const prefix = envPrefix()
  if (prefix === '') return 'api.'
  if (prefix.startsWith('test.')) return 'test.api.'
  if (prefix.startsWith('dev.v2.')) return 'dev.v2.api.'
  return 'dev.api.'
}

/** Where the rail should send someone who clicks `product`.
 *
 *  The branch chain is the account app's sidebar, in the same order and with the same suffixes —
 *  note that InCScreen and InCBot get `/dashboard` appended and InCNudge does not. That asymmetry
 *  is not a mistake to tidy up: it is what those two apps serve, and "fixing" it here would break
 *  two links to make one file look neater.
 *
 *  Returns null for the product we ARE, so the caller can render it as the current item rather than
 *  a link to itself.
 *
 *  ANYTHING WITH NO BRANCH — InCVid, InCFeed, and any product added later — goes to the account
 *  portal's dashboard, which is exactly what the account app's final `<Link to="/dashboard">` does.
 *  A new product therefore lands somewhere real on the day it appears, instead of on a hostname
 *  that was guessed from its name. */
export function productHref(product) {
  const subdomain = String(product?.subdomain || '')
  switch (subdomain.toLowerCase()) {
    case 'incproctor': return null
    // The account app gates this on `getIncserveToken` and sends the browser through a callback
    // that exchanges it. This app has no InCServe token to hand over, so it links to the product
    // root and lets InCServe run its own sign-in — the alternative is hiding the product from the
    // rail entirely, which would make the two sidebars differ.
    case 'incserve':   return `${generateUrl('incserve')}/employer-callback`
    case 'incnudge':   return generateUrl('incnudge')
    case 'incscreen':  return `${generateUrl('incscreen')}/dashboard`
    case 'incbot':     return `${generateUrl('incbot')}/dashboard`
    default:           return `${generateUrl('account')}/dashboard`
  }
}

/** A path on the account portal — used by the rail's "Product Dashboard" and "Support" buttons,
 *  which are the account app's own `/product` and `/support` routes. */
export function accountHref(path) {
  return `${generateUrl('account')}${path}`
}
